/**
 * FLNAssessmentModal — Teacher-side assessment runner.
 *
 * Phases:
 *   mode_select        Choose Student Device | Teacher-Led
 *   teacher_led        Items one-by-one, teacher marks Correct/Incorrect
 *   student_device_rw  Teacher marks Reading/Writing items while student reads on their device
 *   student_device_cnt Waiting for student to complete Counting MCQ
 *   completed          Score + Pass/Fail verdict
 */
import { useState } from "react";
import {
  X, ClipboardCheck, Monitor, Users,
  Loader2, CheckCircle2, XCircle, RefreshCw,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  generateFLNAssessment,
  respondFLNAssessment,
  completeFLNAssessment,
  verdictFLNAssessment,
  getFLNAssessmentSession,
} from "@/lib/api";

const HEAD_LABEL = { reading: "Reading", writing: "Writing", counting: "Counting" };

export default function FLNAssessmentModal({ head, learnerId, level, onClose }) {
  const [phase, setPhase]           = useState("mode_select");
  const [session, setSession]       = useState(null);
  const [loading, setLoading]       = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [rwMarks, setRwMarks]       = useState({});
  const [rwSubmitting, setRwSubmitting] = useState(false);
  const [verdictLoading, setVerdictLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const headLabel = HEAD_LABEL[head];
  const isCounting = head === "counting";
  const items = session?.item_details || [];

  // ── Mode selection ────────────────────────────────────────────────────────
  const handleModeSelect = async (mode) => {
    setLoading(true);
    try {
      const result = await generateFLNAssessment({
        learner_id: learnerId,
        head,
        level,
        delivery_mode: mode,
      });
      setSession(result);
      if (mode === "teacher_led") {
        setPhase("teacher_led");
      } else {
        setPhase(isCounting ? "student_device_cnt" : "student_device_rw");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to generate assessment";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // ── Teacher-Led: mark one item ────────────────────────────────────────────
  const handleTeacherMark = async (isCorrect) => {
    const item = items[currentIdx];
    try {
      const updated = await respondFLNAssessment(session.session_id, item.item_id, isCorrect);
      const isLast = currentIdx === items.length - 1;
      if (isLast) {
        const completed = await completeFLNAssessment(session.session_id);
        setSession(completed);
        setPhase("completed");
      } else {
        setSession(updated);
        setCurrentIdx((i) => i + 1);
      }
    } catch {
      toast.error("Failed to record response");
    }
  };

  // ── Student Device + R/W: toggle mark per item ───────────────────────────
  const handleRWMark = (itemId, isCorrect) =>
    setRwMarks((prev) => ({ ...prev, [itemId]: isCorrect }));

  const handleRWFinalize = async () => {
    setRwSubmitting(true);
    try {
      for (const item of items) {
        const correct = rwMarks[item.item_id] ?? false;
        await respondFLNAssessment(session.session_id, item.item_id, correct);
      }
      const completed = await completeFLNAssessment(session.session_id);
      setSession(completed);
      setPhase("completed");
    } catch {
      toast.error("Failed to finalize assessment");
    } finally {
      setRwSubmitting(false);
    }
  };

  // ── Student Device + Counting: poll for completion ────────────────────────
  const handleCountingRefresh = async () => {
    setRefreshing(true);
    try {
      const updated = await getFLNAssessmentSession(session.session_id);
      setSession(updated);
      if (updated.status === "completed") {
        setPhase("completed");
      } else {
        toast.info("Student hasn't submitted yet.");
      }
    } catch {
      toast.error("Failed to refresh");
    } finally {
      setRefreshing(false);
    }
  };

  // ── Verdict ───────────────────────────────────────────────────────────────
  const handleVerdict = async (verdict) => {
    setVerdictLoading(true);
    try {
      const updated = await verdictFLNAssessment(session.session_id, verdict);
      setSession(updated);
      toast.success(verdict === "pass" ? "Pass recorded." : "Fail recorded.");
    } catch {
      toast.error("Failed to record verdict");
    } finally {
      setVerdictLoading(false);
    }
  };

  // ── Render helpers ────────────────────────────────────────────────────────
  const renderContent = () => {
    switch (phase) {
      /* ── Mode selection ─────────────────── */
      case "mode_select":
        return (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Generate a {Math.min(10, items.length || 10)}-item assessment for{" "}
              <strong className="text-slate-800">{headLabel} · {level}</strong>.
              Choose delivery mode:
            </p>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <button
                  className="flex flex-col items-center gap-2 p-5 rounded-xl border-2 border-slate-200 hover:border-blue-400 hover:bg-blue-50/50 transition-all"
                  onClick={() => handleModeSelect("student_device")}
                  data-testid="fln-assess-mode-student-device"
                >
                  <Monitor className="w-7 h-7 text-blue-500" />
                  <span className="font-semibold text-slate-800 text-sm">Student Device</span>
                  <span className="text-xs text-slate-500 text-center leading-relaxed">
                    Assessment appears on student's FLN tab
                  </span>
                </button>
                <button
                  className="flex flex-col items-center gap-2 p-5 rounded-xl border-2 border-slate-200 hover:border-violet-400 hover:bg-violet-50/50 transition-all"
                  onClick={() => handleModeSelect("teacher_led")}
                  data-testid="fln-assess-mode-teacher-led"
                >
                  <Users className="w-7 h-7 text-violet-500" />
                  <span className="font-semibold text-slate-800 text-sm">Teacher-Led</span>
                  <span className="text-xs text-slate-500 text-center leading-relaxed">
                    Run and mark from this dashboard
                  </span>
                </button>
              </div>
            )}
          </div>
        );

      /* ── Teacher-Led runner ──────────────── */
      case "teacher_led": {
        const item = items[currentIdx];
        if (!item) return null;
        const pct = Math.round(((currentIdx + 1) / items.length) * 100);
        return (
          <div className="space-y-4" data-testid="fln-teacher-led-runner">
            {/* Progress bar */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-violet-500 rounded-full transition-all duration-300"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="text-xs text-slate-400 tabular-nums font-medium">
                {currentIdx + 1}/{items.length}
              </span>
            </div>

            {/* Item card */}
            <Card className="rounded-xl p-4 border border-slate-200 bg-slate-50">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">
                {item.level} · {item.type}
              </p>
              <p
                className="text-base font-medium text-slate-800 leading-relaxed"
                data-testid="fln-assess-item-content"
              >
                {isCounting
                  ? item.question
                  : item.content || item.teacher_prompt || "(No content)"}
              </p>
              {item.teacher_prompt && !isCounting && (
                <p className="text-xs text-slate-500 italic mt-2 border-l-2 border-slate-200 pl-2">
                  {item.teacher_prompt}
                </p>
              )}
            </Card>

            {/* Mark buttons */}
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                className="h-12 border-red-300 text-red-600 hover:bg-red-50 rounded-xl font-semibold gap-2"
                onClick={() => handleTeacherMark(false)}
                data-testid="fln-assess-incorrect-btn"
              >
                <XCircle className="w-4 h-4" /> Incorrect
              </Button>
              <Button
                className="h-12 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold gap-2"
                onClick={() => handleTeacherMark(true)}
                data-testid="fln-assess-correct-btn"
              >
                <CheckCircle2 className="w-4 h-4" /> Correct
              </Button>
            </div>
          </div>
        );
      }

      /* ── Student Device + Counting: waiting ─ */
      case "student_device_cnt":
        return (
          <div className="space-y-4 text-center" data-testid="fln-assess-counting-waiting">
            <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mx-auto">
              <Monitor className="w-7 h-7 text-blue-500" />
            </div>
            <p className="text-sm text-slate-600 leading-relaxed">
              Assessment sent to <strong>{learnerId}</strong>'s FLN tab.<br />
              Student is completing the MCQ on their device.
            </p>
            <Button
              variant="outline"
              className="rounded-xl"
              onClick={handleCountingRefresh}
              disabled={refreshing}
              data-testid="fln-assess-refresh-btn"
            >
              {refreshing
                ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                : <RefreshCw className="w-4 h-4 mr-2" />}
              Check Status
            </Button>
          </div>
        );

      /* ── Student Device + R/W: teacher marks ─ */
      case "student_device_rw": {
        const markedCount = Object.keys(rwMarks).length;
        const allMarked = items.length > 0 && markedCount >= items.length;
        return (
          <div className="space-y-3" data-testid="fln-assess-rw-marking">
            <p className="text-xs text-slate-500 leading-relaxed">
              Student is viewing items on their device. Mark each as you observe them:
            </p>
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {items.map((item, idx) => (
                <div
                  key={item.item_id}
                  className="flex items-start gap-3 p-3 rounded-xl border border-slate-100 bg-slate-50/60"
                  data-testid={`fln-rw-mark-item-${idx}`}
                >
                  <span className="text-xs font-bold text-slate-400 mt-0.5 w-5 flex-shrink-0">
                    {idx + 1}.
                  </span>
                  <p className="flex-1 text-xs text-slate-700 leading-relaxed min-w-0 break-words">
                    {item.content || item.teacher_prompt || `Item ${idx + 1}`}
                  </p>
                  <div className="flex gap-1.5 flex-shrink-0">
                    <button
                      className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
                        rwMarks[item.item_id] === false
                          ? "bg-red-100 text-red-600"
                          : "bg-slate-100 text-slate-300 hover:bg-red-50 hover:text-red-400"
                      }`}
                      onClick={() => handleRWMark(item.item_id, false)}
                      data-testid={`fln-rw-incorrect-${idx}`}
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                    <button
                      className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
                        rwMarks[item.item_id] === true
                          ? "bg-emerald-100 text-emerald-600"
                          : "bg-slate-100 text-slate-300 hover:bg-emerald-50 hover:text-emerald-500"
                      }`}
                      onClick={() => handleRWMark(item.item_id, true)}
                      data-testid={`fln-rw-correct-${idx}`}
                    >
                      <CheckCircle2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between text-xs text-slate-400 pt-1">
              <span>{markedCount}/{items.length} marked</span>
            </div>
            <Button
              className="w-full bg-slate-800 hover:bg-slate-700 text-white rounded-xl h-10"
              disabled={!allMarked || rwSubmitting}
              onClick={handleRWFinalize}
              data-testid="fln-rw-finalize-btn"
            >
              {rwSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Finalize Assessment
            </Button>
          </div>
        );
      }

      /* ── Completed: score + verdict ──────── */
      case "completed": {
        const score = session?.score_percentage ?? 0;
        const total = items.length || 10;
        const correct = Math.round((score / 100) * total);
        const verdict = session?.teacher_verdict;
        const scoreColor =
          score >= 80 ? "text-emerald-600" : score >= 60 ? "text-amber-500" : "text-red-500";
        return (
          <div className="space-y-5" data-testid="fln-assess-result">
            {/* Score */}
            <div className="text-center py-3">
              <div className={`text-5xl font-black ${scoreColor}`} data-testid="fln-assess-score">
                {score}%
              </div>
              <div className="text-sm text-slate-500 mt-1">
                {correct} / {total} correct
              </div>
              <div className="text-xs text-slate-400 mt-0.5">
                {headLabel} · {level}
              </div>
            </div>

            {/* Verdict */}
            {!verdict ? (
              <div className="space-y-2">
                <p className="text-xs text-center text-slate-500 font-semibold uppercase tracking-wider">
                  Record teacher verdict
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant="outline"
                    className="h-11 border-red-300 text-red-600 hover:bg-red-50 rounded-xl font-semibold"
                    onClick={() => handleVerdict("fail")}
                    disabled={verdictLoading}
                    data-testid="fln-assess-fail-btn"
                  >
                    {verdictLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Fail"}
                  </Button>
                  <Button
                    className="h-11 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold"
                    onClick={() => handleVerdict("pass")}
                    disabled={verdictLoading}
                    data-testid="fln-assess-pass-btn"
                  >
                    {verdictLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Pass"}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center space-y-2">
                <div
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-bold text-sm ${
                    verdict === "pass"
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-red-100 text-red-700"
                  }`}
                  data-testid="fln-assess-verdict-display"
                >
                  {verdict === "pass"
                    ? <CheckCircle2 className="w-4 h-4" />
                    : <XCircle className="w-4 h-4" />}
                  {verdict === "pass" ? "Passed" : "Failed"}
                </div>
                {verdict === "pass" && (
                  <p className="text-xs text-emerald-600">
                    Student passed. Click <strong>Advance</strong> on the cursor panel to move to the next level.
                  </p>
                )}
                <Button
                  variant="outline"
                  className="rounded-xl text-sm mt-2"
                  onClick={onClose}
                  data-testid="fln-assess-done-btn"
                >
                  Done
                </Button>
              </div>
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.45)", backdropFilter: "blur(4px)" }}
      data-testid="fln-assessment-modal"
    >
      <Card className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2.5">
            <ClipboardCheck className="w-5 h-5 text-slate-400" />
            <div>
              <h3 className="font-semibold text-slate-800 text-sm leading-tight">
                FLN Assessment
              </h3>
              <p className="text-xs text-slate-400 capitalize">
                {headLabel} · {level}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 transition-colors"
            data-testid="fln-assess-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5">{renderContent()}</div>
      </Card>
    </div>
  );
}
